import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import {
  Activity, Cpu, Users as UsersIcon, SlidersHorizontal, ScrollText,
  ArrowLeft, Plus, Trash2, Zap, Check, Star, KeyRound,
} from "lucide-react";

const TABS = [
  { id: "health", name: "Overview & Health", icon: Activity },
  { id: "providers", name: "Model Providers", icon: Cpu },
  { id: "users", name: "Users", icon: UsersIcon },
  { id: "agents", name: "Agent Config", icon: SlidersHorizontal },
  { id: "logs", name: "Audit Logs", icon: ScrollText },
];

const EMPTY_PROVIDER = { name: "", type: "openai", base_url: "", api_key: "", models: "", use_emergent: false, enabled: true, is_default: false };

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("health");

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-100 flex">
      <aside className="w-64 shrink-0 border-r border-slate-800/70 bg-slate-950/40 fixed h-screen flex flex-col">
        <div className="h-16 flex items-center px-5 border-b border-slate-800/70"><Logo /></div>
        <div className="px-4 py-3 text-xs uppercase tracking-wider text-amber-400/80">Administrator</div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                tab === t.id ? "bg-amber-500/15 text-amber-300 font-medium" : "text-slate-400 hover:text-white hover:bg-slate-800/50"}`}>
              <t.icon className="h-4 w-4" /> {t.name}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800/70">
          <button data-testid="admin-back" onClick={() => navigate("/dashboard")}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800/50">
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </button>
        </div>
      </aside>
      <div className="flex-1 ml-64">
        <div className="h-16 border-b border-slate-800/70 dc-glass sticky top-0 z-30 flex items-center justify-between px-6">
          <h1 className="font-head font-semibold">{TABS.find((t) => t.id === tab)?.name}</h1>
          <ThemeToggle />
        </div>
        <main className="p-6 max-w-6xl mx-auto">
          {tab === "health" && <Health />}
          {tab === "providers" && <Providers />}
          {tab === "users" && <UsersTab currentUser={user} />}
          {tab === "agents" && <AgentConfig />}
          {tab === "logs" && <Logs />}
        </main>
      </div>
    </div>
  );
}

function Health() {
  const [h, setH] = useState({});
  useEffect(() => { api.get("/admin/health").then((r) => setH(r.data)).catch(() => {}); }, []);
  const items = [
    { label: "Users", key: "users" }, { label: "Projects", key: "projects" },
    { label: "Active Builds", key: "active_builds" }, { label: "Total Builds", key: "total_builds" },
    { label: "Providers", key: "providers" }, { label: "Test Runs", key: "test_runs" },
    { label: "Audit Events", key: "audit_events" },
  ];
  return (
    <div className="space-y-6" data-testid="admin-health">
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 flex items-center gap-3">
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 dc-pulse" />
        <span className="font-medium text-emerald-300">System {h.status || "..."}</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((it) => (
          <div key={it.key} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
            <div className="text-3xl font-head font-bold" data-testid={`health-${it.key}`}>{h[it.key] ?? 0}</div>
            <div className="text-sm text-slate-400 mt-1">{it.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Providers() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/admin/providers").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...form, models: typeof form.models === "string" ? form.models.split(",").map((s) => s.trim()).filter(Boolean) : form.models };
    try {
      if (form.id) await api.put(`/admin/providers/${form.id}`, payload);
      else await api.post("/admin/providers", payload);
      toast.success("Provider saved"); setForm(null); load();
    } catch (e) { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete provider?")) return; await api.delete(`/admin/providers/${id}`); load(); };
  const health = async (id) => {
    const { data } = await api.post(`/admin/providers/${id}/health`);
    data.status === "ok" ? toast.success(`Healthy · ${data.detail} · ${data.latency_ms}ms`) : toast.error(`${data.detail}`);
  };

  return (
    <div className="space-y-5" data-testid="admin-providers">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-400">Configure LLM providers. Keys are encrypted; the default provider powers all agents.</p>
        <button data-testid="add-provider" onClick={() => setForm({ ...EMPTY_PROVIDER })}
          className="h-10 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold flex items-center gap-2 text-sm"><Plus className="h-4 w-4" /> Add</button>
      </div>

      <div className="space-y-3">
        {list.map((p) => (
          <div key={p.id} data-testid={`provider-${p.id}`} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-head font-semibold">{p.name}</span>
                {p.is_default && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 flex items-center gap-1"><Star className="h-2.5 w-2.5" /> Default</span>}
                {p.enabled ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">enabled</span> : <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-600/20 text-slate-400">disabled</span>}
              </div>
              <div className="text-xs text-slate-500 mt-1">{p.type} · {p.use_emergent ? "Emergent key" : (p.has_key ? "custom key" : "no key")} · {(p.models || []).join(", ") || "no models"}</div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`health-${p.id}`} onClick={() => health(p.id)} className="h-9 w-9 rounded-lg border border-slate-700 flex items-center justify-center hover:text-emerald-400 hover:border-emerald-500/40" title="Health check"><Zap className="h-4 w-4" /></button>
              <button onClick={() => setForm({ ...p, models: (p.models || []).join(", "), api_key: "" })} className="h-9 px-3 rounded-lg border border-slate-700 text-sm hover:text-cyan-400">Edit</button>
              <button data-testid={`delete-provider-${p.id}`} onClick={() => del(p.id)} className="h-9 w-9 rounded-lg border border-slate-700 flex items-center justify-center hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div className="rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-5 space-y-3" data-testid="provider-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name"><input data-testid="provider-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></Field>
            <Field label="Type">
              <select data-testid="provider-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inp}>
                {["openai", "anthropic", "gemini", "openrouter", "ollama", "custom"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Base URL (for custom/OpenAI-compatible)"><input data-testid="provider-baseurl" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="https://openrouter.ai/api/v1" className={inp} /></Field>
            <Field label="API Key (leave blank to keep)"><input data-testid="provider-apikey" type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} className={inp} /></Field>
          </div>
          <Field label="Models (comma-separated)"><input data-testid="provider-models" value={form.models} onChange={(e) => setForm({ ...form, models: e.target.value })} placeholder="gpt-5.4, gpt-5.4-mini" className={inp} /></Field>
          <div className="flex flex-wrap gap-4">
            <Toggle label="Use Emergent universal key" checked={form.use_emergent} onChange={(v) => setForm({ ...form, use_emergent: v })} testid="provider-emergent" />
            <Toggle label="Enabled" checked={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} testid="provider-enabled" />
            <Toggle label="Default" checked={form.is_default} onChange={(v) => setForm({ ...form, is_default: v })} testid="provider-default" />
          </div>
          <div className="flex gap-2 pt-2">
            <button data-testid="save-provider" onClick={save} className="h-10 px-4 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm">Save</button>
            <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-700 text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function UsersTab({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [nu, setNu] = useState({ email: "", password: "", name: "", role: "user" });
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setRole = async (id, role) => { await api.put(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); load(); };
  const create = async () => {
    if (!nu.email || nu.password.length < 6) return toast.error("Email + 6-char password required");
    try { await api.post("/admin/users", nu); toast.success("User created"); setNu({ email: "", password: "", name: "", role: "user" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this user?")) return; await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); };
  const reset = async (id) => {
    const pw = window.prompt("New password (6+ chars)"); if (!pw) return;
    try { await api.post(`/admin/users/${id}/reset-password`, { password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const inp = "h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm focus:border-emerald-500 focus:outline-none";
  return (
    <div className="space-y-5" data-testid="admin-users">
      <div className="rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-4" data-testid="add-user-form">
        <div className="text-sm font-medium mb-3 flex items-center gap-2"><Plus className="h-4 w-4 text-emerald-400" /> Provision a new account (invite-only)</div>
        <div className="grid sm:grid-cols-5 gap-2">
          <input data-testid="new-user-email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} placeholder="email" className={inp} />
          <input data-testid="new-user-password" type="password" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} placeholder="password" className={inp} />
          <input data-testid="new-user-name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} placeholder="name (optional)" className={inp} />
          <select data-testid="new-user-role" value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })} className={inp}>
            <option value="user">user</option><option value="admin">admin</option>
          </select>
          <button data-testid="create-user" onClick={create} className="h-10 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm">Create</button>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/50 text-slate-400 text-left">
            <tr><th className="p-4">User</th><th className="p-4">Provider</th><th className="p-4">Projects</th><th className="p-4">Role</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-800/60">
                <td className="p-4"><div className="font-medium">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                <td className="p-4 text-slate-400">{u.auth_provider}</td>
                <td className="p-4 text-slate-400">{u.project_count}</td>
                <td className="p-4">
                  <select data-testid={`role-select-${u.id}`} value={u.role} disabled={u.id === currentUser?.id}
                    onChange={(e) => setRole(u.id, e.target.value)}
                    className="h-8 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm disabled:opacity-50">
                    <option value="user">user</option><option value="admin">admin</option>
                  </select>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2 justify-end">
                    <button data-testid={`reset-pw-${u.id}`} onClick={() => reset(u.id)} title="Reset password" className="text-slate-500 hover:text-cyan-400"><KeyRound className="h-4 w-4" /></button>
                    {u.id !== currentUser?.id && <button data-testid={`delete-user-${u.id}`} onClick={() => del(u.id)} title="Delete" className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AgentConfig() {
  const [configs, setConfigs] = useState([]);
  const [providers, setProviders] = useState([]);
  const load = () => {
    api.get("/admin/agent-configs").then((r) => setConfigs(r.data)).catch(() => {});
    api.get("/admin/providers").then((r) => setProviders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const update = async (c, patch) => {
    const next = { ...c, ...patch };
    setConfigs((cs) => cs.map((x) => (x.role === c.role ? next : x)));
    await api.put("/admin/agent-configs", next);
    toast.success(`${c.role} updated`);
  };
  return (
    <div className="space-y-3" data-testid="admin-agents">
      <p className="text-sm text-slate-400">Assign a provider and model to each agent role. Max iterations bound the auto-repair loop.</p>
      {configs.map((c) => (
        <div key={c.role} data-testid={`agent-config-${c.role}`} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 flex flex-wrap items-center gap-4">
          <div className="w-28 font-head font-semibold capitalize">{c.role}</div>
          <select value={c.provider_id || ""} onChange={(e) => update(c, { provider_id: e.target.value })}
            className="h-9 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm">
            <option value="">Default provider</option>
            {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input value={c.model || ""} onChange={(e) => update(c, { model: e.target.value })} placeholder="model"
            className="h-9 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm w-40" />
          <div className="flex items-center gap-2 text-sm text-slate-400">
            max iter
            <input type="number" min={1} max={10} value={c.max_iterations} onChange={(e) => update(c, { max_iterations: +e.target.value })}
              className="h-9 w-16 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm" />
          </div>
          <Toggle label="enabled" checked={c.enabled} onChange={(v) => update(c, { enabled: v })} testid={`agent-enabled-${c.role}`} />
        </div>
      ))}
    </div>
  );
}

function Logs() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/logs?limit=150").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  const color = { info: "text-slate-400", warning: "text-amber-400", error: "text-rose-400" };
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4 font-mono text-xs space-y-1 dc-scroll overflow-auto max-h-[70vh]" data-testid="admin-logs">
      {logs.length === 0 && <div className="text-slate-500">No events yet.</div>}
      {logs.map((l) => (
        <div key={l.id} className="flex gap-3">
          <span className="text-slate-600 shrink-0">{new Date(l.created_at).toLocaleTimeString()}</span>
          <span className={`${color[l.level] || color.info} shrink-0 w-40 truncate`}>{l.action}</span>
          <span className="text-slate-500 truncate">{l.target || ""}</span>
        </div>
      ))}
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm focus:border-emerald-500 focus:outline-none";
function Field({ label, children }) { return <label className="block"><span className="text-xs text-slate-400 mb-1 block">{label}</span>{children}</label>; }
function Toggle({ label, checked, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)} className="flex items-center gap-2 text-sm">
      <span className={`h-5 w-9 rounded-full transition-colors relative ${checked ? "bg-emerald-500" : "bg-slate-700"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${checked ? "left-4.5" : "left-0.5"}`} style={{ left: checked ? "1.125rem" : "0.125rem" }} />
      </span>
      <span className="text-slate-300">{label}</span>
    </button>
  );
}
